/**
 * useAgenticApi — React hooks for Pipeline Engineer agentic endpoints.
 *
 * All hooks use plain useState/useEffect/useCallback (no React Query dependency).
 * Polling follows the chained-setTimeout + pollTick pattern from RunPanel.tsx.
 *
 * POLLING INVARIANTS:
 *   - POLL_INTERVAL_MS = 3000 (matches RunPanel.tsx)
 *   - POLL_TIMEOUT_MS = 60_000 (60 seconds max before timeout error)
 *   - Terminal statuses: "completed" and "failed" — polling stops immediately
 *   - On transient network error: pollTick increments to retry (never silently stops)
 *
 * SAFETY INVARIANT:
 *   - All hooks return only schema metadata (names, types) — never data values.
 *   - Components using these hooks must not render SENSITIVE_KEYS fields.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  triggerIntrospection,
  pollTask,
  proposeJob,
  pollProposal,
  acceptProposal,
  rejectProposal,
  suggestCTESteps,
  refineCTEStep,
  nlToSQL,
} from "./api/pe-client";
import type {
  PETask,
  PEProposal,
  TaskStatus,
  CTESuggestionResult,
  CTERefineResult,
  NLToSQLResult,
  CTEStep,
} from "./types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60_000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isTerminalTaskStatus(status: TaskStatus): boolean {
  return status === "completed" || status === "failed";
}

// ---------------------------------------------------------------------------
// useIntrospectTask
// ---------------------------------------------------------------------------

export interface IntrospectTaskState {
  /** null when no task is in flight */
  taskId: string | null;
  task: PETask | null;
  loading: boolean;
  error: string | null;
  /** Start a new introspection — resets all state */
  start: (
    tableNames?: string[] | null,
    schemaName?: string | null,
  ) => Promise<void>;
  /** Reset to idle state */
  reset: () => void;
}

/**
 * Manages the full lifecycle of a schema introspection task.
 * Triggers POST /introspect, then polls GET /tasks/{task_id} until terminal.
 * Pass side="target" for a separate hook instance introspecting the target
 * schema (needed by the Auto-Map mapping mode, which requires both).
 */
export function useIntrospectTask(
  pipelineId: string,
  projectId: string,
  side: "source" | "target" = "source",
): IntrospectTaskState {
  const [taskId, setTaskId] = useState<string | null>(null);
  const [task, setTask] = useState<PETask | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const pollStartTime = useRef<number>(0);

  // Polling effect — fires whenever taskId, task status, or pollTick changes.
  useEffect(() => {
    if (taskId === null || task === null) return;
    if (isTerminalTaskStatus(task.status)) return;

    const elapsed = Date.now() - pollStartTime.current;
    if (elapsed >= POLL_TIMEOUT_MS) {
      setError("Introspection timed out after 60 seconds. Please retry.");
      setLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const updated = await pollTask(taskId);
        setTask(updated);
        if (isTerminalTaskStatus(updated.status)) {
          setLoading(false);
          if (updated.status === "failed") {
            setError(updated.error ?? "Introspection failed.");
          }
        }
      } catch {
        // Transient network error — increment tick to retry
        setPollTick((t) => t + 1);
      }
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [taskId, task, pollTick]);

  const start = useCallback(
    async (tableNames?: string[] | null, schemaName?: string | null) => {
      setError(null);
      setTask(null);
      setTaskId(null);
      setLoading(true);
      setPollTick(0);
      pollStartTime.current = Date.now();
      try {
        const { taskId: newTaskId } = await triggerIntrospection(
          pipelineId,
          projectId,
          tableNames ?? null,
          side,
          schemaName ?? null,
        );
        setTaskId(newTaskId);
        // Seed with a minimal pending task so the polling effect fires
        setTask({
          taskId: newTaskId,
          status: "pending",
          taskType: "introspect",
          result: null,
          error: null,
        });
      } catch (err) {
        setLoading(false);
        setError(
          err instanceof Error ? err.message : "Failed to start introspection.",
        );
      }
    },
    [pipelineId, projectId, side],
  );

  const reset = useCallback(() => {
    setTaskId(null);
    setTask(null);
    setLoading(false);
    setError(null);
    setPollTick(0);
  }, []);

  return { taskId, task, loading, error, start, reset };
}

// ---------------------------------------------------------------------------
// useProposeJob
// ---------------------------------------------------------------------------

export interface ProposeJobState {
  proposalId: string | null;
  proposal: PEProposal | null;
  loading: boolean;
  error: string | null;
  /** Start a new proposal — resets all state */
  start: (taskId: string, userPrompt: string) => Promise<void>;
  /** Reset to idle state */
  reset: () => void;
}

/**
 * Manages the full lifecycle of a job proposal task.
 * Triggers POST /propose-jobs, then polls GET /proposals/{id} until task_status is terminal.
 */
export function useProposeJob(
  pipelineId: string,
  projectId: string,
): ProposeJobState {
  const [proposalId, setProposalId] = useState<string | null>(null);
  const [proposal, setProposal] = useState<PEProposal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const pollStartTime = useRef<number>(0);

  useEffect(() => {
    if (proposalId === null || proposal === null) return;
    if (isTerminalTaskStatus(proposal.taskStatus)) return;

    const elapsed = Date.now() - pollStartTime.current;
    if (elapsed >= POLL_TIMEOUT_MS) {
      setError("Proposal generation timed out after 60 seconds. Please retry.");
      setLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const updated = await pollProposal(proposalId);
        setProposal(updated);
        if (isTerminalTaskStatus(updated.taskStatus)) {
          setLoading(false);
          if (updated.taskStatus === "failed") {
            setError(updated.error ?? "Proposal generation failed.");
          }
        }
      } catch {
        setPollTick((t) => t + 1);
      }
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [proposalId, proposal, pollTick]);

  const start = useCallback(
    async (taskId: string, userPrompt: string) => {
      setError(null);
      setProposal(null);
      setProposalId(null);
      setLoading(true);
      setPollTick(0);
      pollStartTime.current = Date.now();
      try {
        const { proposalId: newProposalId } = await proposeJob(
          pipelineId,
          taskId,
          userPrompt,
          projectId,
        );
        setProposalId(newProposalId);
        setProposal({
          proposalId: newProposalId,
          status: "pending",
          taskStatus: "pending",
          items: null,
          error: null,
        });
      } catch (err) {
        setLoading(false);
        setError(
          err instanceof Error ? err.message : "Failed to start job proposal.",
        );
      }
    },
    [pipelineId, projectId],
  );

  const reset = useCallback(() => {
    setProposalId(null);
    setProposal(null);
    setLoading(false);
    setError(null);
    setPollTick(0);
  }, []);

  return { proposalId, proposal, loading, error, start, reset };
}

// ---------------------------------------------------------------------------
// useAcceptProposal
// ---------------------------------------------------------------------------

export interface AcceptProposalState {
  loading: boolean;
  error: string | null;
  jobsCreated: number | null;
  accept: (proposalId: string) => Promise<void>;
}

/** Calls POST /proposals/{id}/accept and tracks loading/error/result. */
export function useAcceptProposal(
  onSuccess?: (jobsCreated: number) => void,
): AcceptProposalState {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobsCreated, setJobsCreated] = useState<number | null>(null);

  const accept = useCallback(
    async (proposalId: string) => {
      setError(null);
      setJobsCreated(null);
      setLoading(true);
      try {
        const result = await acceptProposal(proposalId);
        setJobsCreated(result.jobsCreated);
        onSuccess?.(result.jobsCreated);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to accept proposal.",
        );
      } finally {
        setLoading(false);
      }
    },
    [onSuccess],
  );

  return { loading, error, jobsCreated, accept };
}

// ---------------------------------------------------------------------------
// useRejectProposal
// ---------------------------------------------------------------------------

export interface RejectProposalState {
  loading: boolean;
  error: string | null;
  reject: (proposalId: string) => Promise<void>;
}

/** Calls POST /proposals/{id}/reject and tracks loading/error. */
export function useRejectProposal(onSuccess?: () => void): RejectProposalState {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reject = useCallback(
    async (proposalId: string) => {
      setError(null);
      setLoading(true);
      try {
        await rejectProposal(proposalId);
        onSuccess?.();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to reject proposal.",
        );
      } finally {
        setLoading(false);
      }
    },
    [onSuccess],
  );

  return { loading, error, reject };
}

// ---------------------------------------------------------------------------
// useGenerateCTE
// ---------------------------------------------------------------------------

export interface GenerateCTEState {
  result: CTESuggestionResult | null;
  loading: boolean;
  error: string | null;
  generate: (hints?: string | null) => Promise<void>;
  reset: () => void;
}

/** Calls POST /jobs/{job_id}/generate-cte and tracks loading/error/result. */
export function useGenerateCTE(
  jobId: string,
  projectId: string,
): GenerateCTEState {
  const [result, setResult] = useState<CTESuggestionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(
    async (hints?: string | null) => {
      setError(null);
      setResult(null);
      setLoading(true);
      try {
        const data = await suggestCTESteps(jobId, projectId, hints ?? null);
        setResult(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to generate CTE steps.",
        );
      } finally {
        setLoading(false);
      }
    },
    [jobId, projectId],
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, generate, reset };
}

// ---------------------------------------------------------------------------
// useRefineCTEStep
// ---------------------------------------------------------------------------

export interface RefineCTEStepState {
  result: CTERefineResult | null;
  loading: boolean;
  error: string | null;
  refine: (
    stepName: string,
    currentSql: string,
    instruction: string,
    currentCteSteps: CTEStep[],
  ) => Promise<void>;
  reset: () => void;
}

/** Calls POST /jobs/{job_id}/refine-cte and tracks loading/error/result. */
export function useRefineCTEStep(
  jobId: string,
  projectId: string,
): RefineCTEStepState {
  const [result, setResult] = useState<CTERefineResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refine = useCallback(
    async (
      stepName: string,
      currentSql: string,
      instruction: string,
      currentCteSteps: CTEStep[],
    ) => {
      setError(null);
      setResult(null);
      setLoading(true);
      try {
        const data = await refineCTEStep(
          jobId,
          projectId,
          stepName,
          currentSql,
          instruction,
          currentCteSteps,
        );
        setResult(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to refine CTE step.",
        );
      } finally {
        setLoading(false);
      }
    },
    [jobId, projectId],
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, refine, reset };
}

// ---------------------------------------------------------------------------
// useNLtoSQL
// ---------------------------------------------------------------------------

export interface NLtoSQLState {
  result: NLToSQLResult | null;
  loading: boolean;
  translate: (targetCol: string, nlDescription: string) => Promise<void>;
  reset: () => void;
}

/**
 * Calls POST /jobs/{job_id}/nl-to-sql.
 * Returns {expression, error} — expression is null when error is non-null.
 * Never throws — errors surface via result.error or the loading state.
 */
export function useNLtoSQL(jobId: string, projectId: string): NLtoSQLState {
  const [result, setResult] = useState<NLToSQLResult | null>(null);
  const [loading, setLoading] = useState(false);

  const translate = useCallback(
    async (targetCol: string, nlDescription: string) => {
      setResult(null);
      setLoading(true);
      try {
        const data = await nlToSQL(jobId, projectId, targetCol, nlDescription);
        setResult(data);
      } catch (err) {
        // Surface network/HTTP errors as a result.error so the component
        // always has a consistent result shape to render.
        setResult({
          expression: null,
          error: err instanceof Error ? err.message : "Translation failed.",
        });
      } finally {
        setLoading(false);
      }
    },
    [jobId, projectId],
  );

  const reset = useCallback(() => {
    setResult(null);
  }, []);

  return { result, loading, translate, reset };
}
